import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../components/sudanLanding.scss";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ClosingCTA from "../components/ClosingCTA";
import CollabImg from "../assets/capacity-collaboration.jpg";
import HeroShot from "../sudanimg/img (11).png";
import { LanguageProvider, useLanguage } from "../i18n/LanguageContext";

/* Step icons — monitor-based glyphs matching the Sudan teal/blue palette,
   mirroring the "How KAPorg works" section on kaporg.com/product/how-it-works */
const IconSetup = () => (
   <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="14" width="48" height="30" rx="3" stroke="#00266a" strokeWidth="2.5" />
      <path d="M22 56h20M32 44v12" stroke="#00266a" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="24" cy="27" r="5" fill="#c0f9f8" stroke="#035970" strokeWidth="2" />
      <circle cx="40" cy="27" r="5" fill="#c0f9f8" stroke="#035970" strokeWidth="2" />
      <path d="M17 38c1.5-4 5-6 7-6s5.5 2 7 6M33 38c1.5-4 5-6 7-6s5.5 2 7 6" stroke="#035970" strokeWidth="2" strokeLinecap="round" />
   </svg>
);
const IconDesign = () => (
   <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="14" width="48" height="30" rx="3" stroke="#00266a" strokeWidth="2.5" />
      <path d="M22 56h20M32 44v12" stroke="#00266a" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M18 24h18M18 30h22M18 36h14" stroke="#035970" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="46" cy="20" r="8" fill="#c0f9f8" stroke="#035970" strokeWidth="2" />
      <path d="M43 20l2 2 4-4" stroke="#035970" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
   </svg>
);
const IconMonitor = () => (
   <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="14" width="48" height="30" rx="3" stroke="#00266a" strokeWidth="2.5" />
      <path d="M22 56h20M32 44v12" stroke="#00266a" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M18 36l7-8 6 5 8-11 7 8" stroke="#035970" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="46" cy="30" r="3" fill="#c0f9f8" stroke="#035970" strokeWidth="2" />
   </svg>
);
const IconAction = () => (
   <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="14" width="48" height="30" rx="3" stroke="#00266a" strokeWidth="2.5" />
      <path d="M22 56h20M32 44v12" stroke="#00266a" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="32" cy="29" r="10" fill="#c0f9f8" stroke="#035970" strokeWidth="2" />
      <path d="M32 24v5l4 3" stroke="#035970" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
   </svg>
);
const IconLearn = () => (
   <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="14" width="48" height="30" rx="3" stroke="#00266a" strokeWidth="2.5" />
      <path d="M22 56h20M32 44v12" stroke="#00266a" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M32 22l-11 4 11 4 11-4-11-4z" fill="#c0f9f8" stroke="#035970" strokeWidth="2" strokeLinejoin="round" />
      <path d="M26 28v6c0 1.7 2.7 3 6 3s6-1.3 6-3v-6M43 26v7" stroke="#035970" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
   </svg>
);

/* mirrors the "How it works" section on kaporg.com/product/how-it-works */
const HIW_STEPS = [
   {
      num: "01",
      title: "Set up your organization",
      text: "Create your organization profile, add users, and set up workspaces aligned with your structure, teams, or focus areas.",
      Icon: IconSetup
   },
   {
      num: "02",
      title: "Design and launch assessments",
      text: "Build tailored questionnaires and deploy them across departments, partners, or locations to evaluate internal performance and capacity.",
      Icon: IconDesign
   },
   {
      num: "03",
      title: "Monitor results in real time",
      text: "Access dashboards and reports that highlight strengths, expose gaps, and compare performance across units or teams.",
      Icon: IconMonitor
   },
   {
      num: "04",
      title: "Drive improvement with action plans",
      text: "Turn insights into action by assigning tasks, setting deadlines, and tracking progress directly within the platform.",
      Icon: IconAction
   },
   {
      num: "05",
      title: "Learn and progress",
      text: "Create your own knowledge hub and share resources with your team to improve performance and achieve your goals.",
      Icon: IconLearn
   }
];

/* four capability cards arranged around a central photo, mirroring kaporg.com */
const HIW_CARDS = [
   {
      title: "Platform Flexibility",
      text: "KAPorg adapts to your structure, timelines, and goals. Customize assessments, surveys, and user roles to fit your context.",
      points: ["Templates", "Scoring", "Rounds", "Permissions"]
   },
   {
      title: "Collaborate Securely",
      text: "KAPorg lets you assign user roles with permission-based access tailored to your structure.",
      points: ["Assessment Administrators", "Unit Managers", "Staff Members", "External Stakeholders"]
   },
   {
      title: "Stay on Track",
      text: "KAPorg sends automated reminders for deadlines, task updates, and new submissions so everyone stays informed and nothing gets missed.",
      points: ["Submission Alerts", "Task Reminders", "Deadline Notifications", "Status Updates"]
   },
   {
      title: "See What Matters",
      text: "KAPorg transforms your assessment data into interactive dashboards and exportable reports that highlight strengths, gaps, and trends across teams or institutions.",
      points: ["Real-Time Dashboards", "Comparative Reporting", "Exportable PDFs", "Visual Trends and Gaps"]
   }
];

const HowItWorksInner = () => {
   const rootRef = useRef(null);
   const { t, dir, lang } = useLanguage();

   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   /* reveal-on-scroll: add .is-visible when a [data-reveal] element enters view */
   useEffect(() => {
      const nodes = rootRef.current?.querySelectorAll("[data-reveal]") || [];
      if (!("IntersectionObserver" in window)) {
         nodes.forEach((n) => n.classList.add("is-visible"));
         return;
      }
      const observer = new IntersectionObserver(
         (entries) => {
            entries.forEach((entry) => {
               if (entry.isIntersecting) {
                  entry.target.classList.add("is-visible");
                  observer.unobserve(entry.target);
               }
            });
         },
         { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
      );
      nodes.forEach((n) => observer.observe(n));
      return () => observer.disconnect();
   }, []);

   return (
      <div className="App">
         <div
            className={`sudan-kaporg sudan-landing ${lang === "ar" ? "sudan-rtl" : ""}`}
            dir={dir}
            ref={rootRef}
         >
            <Navbar />

            <section className="sd-page-hero sd-hero-hiw">
               <div className="container">
                  <div className="sd-page-hero-inner">
                     <div className="sd-page-hero-text">
                        <span className="sd-chip">{t("How it works")}</span>
                        <h1>{t("From your first login to a recognized certificate.")}</h1>
                        <p>
                           {t(
                              "KAPorg walks Sudanese organizations through a structured pathway that builds, documents, and certifies organizational capacity — entirely free."
                           )}
                        </p>
                        <div className="sd-page-hero-cta">
                           <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                              {t("Register for free")}
                           </Link>
                           <Link
                              to="/capacity-accelerator-program"
                              className="sd-btn sd-btn-ghost-light"
                           >
                              {t("Capacity Accelerator Program")}
                           </Link>
                        </div>
                     </div>

                     <div className="sd-page-hero-visual sd-hero-hiw-visual">
                        <div className="sd-hero-shot">
                           <img src={HeroShot} alt="KAPorg results dashboard" />
                        </div>
                        <span className="sd-hero-float sd-hero-float-1">
                           <b>01</b> {t("Set up in minutes")}
                        </span>
                        <span className="sd-hero-float sd-hero-float-2">
                           <b>{t("Live")}</b> {t("Real-time results")}
                        </span>
                     </div>
                  </div>
               </div>
            </section>

            {/* How KAPorg works — zigzag timeline, same section as kaporg.com/product/how-it-works */}
            <section className="sd-section sd-bg-white" id="how-it-works">
               <div className="container">
                  <div className="sd-section-head">
                     <h2>{t("How KAPorg works")}</h2>
                     <p>
                        {t(
                           "From setup to action planning, KAPorg helps you assess internal performance, identify gaps, and manage progress across teams and institutions. Discover how organizations use the platform to design assessments, generate insights, and drive meaningful change at every stage."
                        )}
                     </p>
                  </div>

                  <div className="sd-hiw-timeline">
                     {HIW_STEPS.map((step, index) => (
                        <div
                           className={`sd-hiw-row ${index % 2 === 0 ? "is-left" : "is-right"}`}
                           key={step.num}
                           data-reveal
                           style={{ transitionDelay: `${index * 90}ms` }}
                        >
                           <div className="sd-hiw-text">
                              <h4>{t(step.title)}</h4>
                              <p>{t(step.text)}</p>
                           </div>
                           <div className="sd-hiw-marker">
                              <span className="sd-hiw-dot">{step.num}</span>
                           </div>
                           <div className="sd-hiw-icon">
                              <step.Icon />
                           </div>
                        </div>
                     ))}
                  </div>
               </div>
            </section>

            {/* Capability cards around a central photo */}
            <section className="sd-section sd-hiw-cards-section">
               <div className="container">
                  <div className="sd-hiw-cards">
                     {HIW_CARDS.map((card, index) => (
                        <div
                           className="sd-hiw-card"
                           key={card.title}
                           data-reveal
                           style={{ transitionDelay: `${index * 90}ms` }}
                        >
                           <h3>{t(card.title)}</h3>
                           <p>{t(card.text)}</p>
                           <ul>
                              {card.points.map((p) => (
                                 <li key={p}>{t(p)}</li>
                              ))}
                           </ul>
                        </div>
                     ))}

                     {/* photo pinned to the centre column, spanning both card rows */}
                     <div className="sd-hiw-card-media" data-reveal>
                        <img src={CollabImg} alt="A team collaborating on KAPorg" />
                     </div>
                  </div>

                  <div
                     style={{
                        display: "flex",
                        gap: "16px",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        marginTop: "56px"
                     }}
                  >
                     <Link to="/auth/registration" className="sd-btn sd-btn-primary">
                        {t("Register for free")}
                     </Link>
                     <a href="mailto:info@kaptechgroup.com" className="sd-btn sd-btn-secondary">
                        {t("Book a demo")}
                     </a>
                  </div>
               </div>
            </section>

            <ClosingCTA />
            <Footer />
         </div>
      </div>
   );
};

const HowItWorks = () => (
   <LanguageProvider>
      <HowItWorksInner />
   </LanguageProvider>
);

export default HowItWorks;
